<template>
  <ul class="swatch-list" @mouseleave="hovered = null">
    <li
      v-for="(swatch, index) in swatches"
      :key="swatch.hex"
      :style="{ '--swatch-angle': angle(index), '--swatch-dir': direction(index) }"
      @mouseenter="hovered = index"
    >
      <span
        class="swatch-chip"
        :class="{ 'is-ink-light': lightInk(swatch.hex) }"
        :style="{ background: swatch.hex }"
      >
        <span class="swatch-hex">{{ swatch.hex }}</span>
        <span v-if="swatch.name" class="swatch-name">{{ swatch.name }}</span>
      </span>
    </li>
  </ul>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

/* A colour palette, as a deck of cards that fans open and comes forward when
   read.

   It came out of the brand presence page, where it was `.bp-swatches`, and the
   portfolio case study is its second reader: a project's palette is the same
   object whether the page is selling a brand kit or recounting a build, and two
   pages showing the same six colours under two class names is how the card
   width, the radius and the hover end up a step apart.

   The cards overlap into one deck rather than sitting apart as a row of chips:
   every card after the first pulls left under the one before it, so the row reads
   as a hand of samples held in one hand rather than as six boxes with gaps
   between them. At rest every card is square and the deck is a tidy stack. The
   lean is not a resting state at all, so the only thing that has to know where a
   card sits is the open state below, where the angle travels as a custom property
   and the stylesheet declares the transform that uses it.

   The stacking order needs nothing but the markup: every card is positioned, and
   positioned siblings paint in document order, so the later card already covers
   the earlier one. That is why no card declares a `z-index`, at rest or under
   the pointer.

   Pointing at a card opens the deck around it, and the fan is stepped from the
   picker rather than laid over a fixed arc. Every other card takes the picker's
   position as its origin and leans by its own distance from it, so the steps are
   even all the way across: the two either side of the picker lean the same
   amount in opposite directions, the two beyond those lean twice that, and the
   deck re-centres as the pointer moves instead of the picker having to land on
   an arc that was drawn for the middle of the deck.

   That is the whole reason the angle is computed from the hovered index. The old
   fan gave every card a fixed arc angle and then pushed the wings a flat 5deg
   past it, which meant the picker squared up to 0 while its neighbour kept its
   arc minus 5, and the two gaps either side of the picker could not come out
   equal: a four-colour deck opened at -11, 0, 7, 11, so the single card on the
   left was flung three times as far as the one on the right and the deck read as
   toppling rather than fanning.

   The picker itself stays square, lifts a little and shows its own detail, so its
   colour faces the reader head on. It stays in document order rather than rising
   over its neighbours, which is why nothing here carries a `z-index`: the deck
   opening around it is what uncovers it.

   Only `transform` moves, and the card width, the overlap and the row height are
   fixed, so the deck never reflows and the page below it never jitters as the
   pointer crosses. The open state is bound to `:has()` on the list, so the whole
   deck is one gesture: the pointer opens it and no card has to be crossed for the
   rest of the deck to follow.

   The two halves of the movement are owned in different places on purpose. The
   angle is a fact about the deck's order, which only the markup knows, so it is
   computed and handed over as a property. The slide is a distance that has to
   answer to the column the deck is standing in, which only the stylesheet knows,
   so it stays a length here and the script only gives it a direction. Below the
   smallest breakpoint the cards shrink and the slide has to shrink with them or
   the outermost card leaves its column, and that is a media query rather than
   arithmetic in the script.

   Nothing here is a link: this is a specimen, not a chooser. */
.swatch-list {
  /* How far a wing card travels clear of the picker. This is the stylesheet's
     half of the movement and the only part of the opening that is a length, so it
     is the only part that changes at a breakpoint.

     It is capped by the room the deck stands in rather than by taste: see the
     note above the opening rule for why it stops where it does. */
  --swatch-slide: 1.3rem;

  /* The two inks a card can be read in, published as custom properties so the
     script can measure a swatch against the real pair rather than against an
     assumed one. They are the same two tokens the rest of the site reads on, so
     the deck declares no colour of its own and a change to either token reaches
     every card. */
  --swatch-ink-dark: #{$ink};
  --swatch-ink-light: #{$on-dark-strong};

  /* The deck takes whatever height its reader gives it. On its own that is the
     card's own height, so nothing changes; the spec plate makes its group a
     column and hands the deck the leftover, which is what lets a four-card
     column end level with the typeface column beside it instead of stopping
     short and leaving the gap under it. */
  align-items: stretch;
  display: flex;
  flex: 1 1 auto;
  list-style: none;
  margin: 0;
  /* Room for the fan, so a wing can never reach the column beside the deck.

     The deck is left-aligned in its group, so a card that leans and slides out
     travels left of the group's own edge, and there was nothing between it and
     the type specimen in the column next door but the row's gap. The travel is
     arithmetic rather than a guess: a wing slides 1.3rem clear of the picker and
     its top corner swings out by a card's height times the sine of its lean,
     which at the widest step is another 0.7rem, so the furthest a card reaches is
     2rem on the four-colour decks every case study carries and 2.5rem on the
     brand kit's six.

     Two rem is therefore the inset, and it is not free either: it comes out of
     the same column the deck has to fit, which is why the card below is the size
     it is. On the six-colour deck the swing asks for half a rem more than the
     inset gives it, and that half rem is spent in the brand kit's own board
     padding, which is what that padding is there for.

     The inset is on the list rather than on the group so the deck carries its own
     margin as a component, and the label above it keeps the line that every
     group's label in the row shares. */
  padding: 0 2rem;

  li {
    /* A column, so the card is the thing that gives when the deck has more
       height than one card needs: the card fills the row rather than sitting at
       its top. Its width comes from the row, so nothing here has to state it. */
    display: flex;
    flex-direction: column;
    flex: 0 0 auto;
    position: relative;
    /* The width is what the narrowest column the deck has to live in will
       allow. Both readers put it in a two-column grid, and the tighter of the
       two is the portfolio spec sheet, where the group is about 320px: with the
       list's own 2rem inset taken off each side that leaves 16rem, and a
       four-card deck has to come in under it. The deck is kept narrow by
       deepening the overlap rather than by holding the card back, which is what
       lets the card itself be as large as it is. */
    width: 6.2rem;

    /* The overlap itself. Every card but the first tucks under the one before
       it, which is what turns four wide cards into one object narrow enough for
       the column: at 3.6rem each card still leaves 2.6rem of colour showing, and
       the deck comes out a tenth of a rem narrower than it was when the card was
       5.4rem against a 2.5rem overlap, because the eight tenths the card gained
       are more than the eight tenths the overlap gained. A larger card in a deck
       no wider is the only way a larger card fits the same column. */
    & + li {
      margin-left: -3.6rem;
    }

    &:hover {
      /* The picker, and the one card that does not take the deck's lean: it stays
         square, so its colour faces the reader head on, and it lifts a little
         and takes the firmer edge. It stays in document order rather than rising
         over its neighbours, which is why nothing here carries a `z-index`: the
         deck opening around it is what uncovers it. The lift and the opening
         share one duration and easing, so the gesture reads as a single movement
         rather than two. */
      .swatch-chip {
        border-color: $line-strong;
        box-shadow: $shadow-lift;
        transform: translateY(-0.6rem) scale(1.03);

        .swatch-hex,
        .swatch-name {
          opacity: 1;
        }
      }
    }
  }

  /* The deck opening, and the one place the movement is declared. Nothing turns
     or slides a card at rest, so both are written here and nowhere else: the
     angle and the direction arrive as the custom properties the cards were
     handed, and the distance is the length above.

     It is also the one place the deck needs a selector the markup order cannot
     express. A sibling combinator only reaches forwards, so the cards *before*
     the hovered one can only be named with `:has(~ li:hover)`; there is no
     backwards `~`. It is the only `:has()` in the front end, and both wings hang
     off the same `:has()` gate rather than only the wing that needs it, so a
     browser that cannot parse `:has()` leaves the deck stacked instead of opening
     one side of it.

     Each wing clears the picker by sliding out the same distance rather than one
     that grows with its distance from the pointer, because a card shows 2.9rem of
     colour at rest and about 4.2rem with its wing out, so a sliver of overlap is
     left rather than a gap, and a deck that opens as one piece is what keeps it
     calm under a pointer that keeps moving.

     That distance is also the one thing the deck can get wrong on its own, which
     is why it is the one number here that had to be argued rather than chosen. A
     wing travels into whatever inset the deck is standing in, and the two readers
     do not offer the same room: the portfolio column leaves the deck a 2rem grid
     gap, and the brand kit sets it inside a board with 1.1rem of padding. The
     tighter of the two is therefore the ceiling, and 1.3rem is that ceiling once
     the brand kit's board has been given the padding to hold its own fan. Left at
     1.1rem the outermost card of a fanned deck crossed the board's border, which
     reads as a rendering fault rather than as a hand of samples, so the board was
     given 1.5rem instead: the inset has to be wider than the travel, not equal to
     it, and the 3.2px left over is the margin that keeps a subpixel rounding from
     putting a card's edge on the border line again.

     The gate is `:has(li:hover)` rather than a hover flag on the list, so the deck
     can never stand open with no picker in it: a pointer that is over the row but
     not over a card leaves the gate shut, and the angles the script is holding
     are simply not read. */
  &:has(li:hover) li:not(:hover) .swatch-chip {
    transform: translateX(calc(var(--swatch-slide) * var(--swatch-dir, 0))) rotate(var(--swatch-angle, 0deg));
  }

  /* A deck is compact, so it does not need the old grid's second row. It does
     have to stay on one line inside a phone column, though, so below the
     smallest breakpoint the card shrinks, the overlap shrinks with it, the code
     drops a step and the wing's slide shortens: six cards at the desktop size
     would overflow the narrowest phone column, which a deck must never do. The
     angle needs nothing here, because a degree is a degree at any width. */
  @media (max-width: 575.98px) {
    --swatch-slide: 0.65rem;

    li {
      width: 3.15rem;

      & + li {
        margin-left: -1.45rem;
      }
    }

    .swatch-chip {
      min-height: 6.3rem;
      padding: 0.4rem 0.25rem;
    }

    .swatch-hex {
      font-size: 0.54rem;
    }

    .swatch-name {
      font-size: 0.6rem;
    }
  }

  /* The deck reveals the code and the name when the pointer reaches a card, and
     a coarse pointer has no hover to arrive with, so on a touch device both are
     simply held open. The deck keeps its anatomy and its colours; only the
     reveal step is skipped. */
  @media (pointer: coarse) {
    .swatch-hex,
    .swatch-name {
      opacity: 1;
    }
  }
}

/* The card, and the ink the detail is read in.

   The hairline border is what keeps a near-white card from vanishing into the
   surface behind it. Flex column rather than block now that the code and the
   name both sit in it, so the two stack without colliding and centre in the
   colour they belong to.

   The text sits straight on the swatch. It used to sit on a scrim: a fill of the
   site's own ink at 62% laid over the card whenever the pointer arrived, because
   a palette holds near-white and near-black in the same deck and one fixed ink
   could not read on both. The scrim solved the reading but it also threw the
   colour away at the exact moment the reader asked to see it, which is the one
   thing the deck exists to show. So the scrim is gone and the ink is chosen
   instead: the component measures each swatch and hands the card the one of the
   two site inks that reads on it.

   The two inks are `$ink` and `$on-dark-strong`, the same pair the rest of the
   site reads on, and which one a card takes is the only decision the script makes
   about colour. It is a choice between two tokens rather than a colour computed
   in the script, so no hex is declared here and a change to either token still
   reaches every card.

   The transform and its shadow share the slower step, because the lift is one
   movement and the shadow must not land before the card finishes rising. The
   hairline is a colour rather than a movement, so it keeps the quick step. */
.swatch-chip {
  align-items: center;
  border: 1px solid $line;
  border-radius: $r-sm;
  /* The ink for a light swatch. The dark chip below is the other half. Together
     they are both of the inks this component can use. */
  color: $ink;
  display: flex;
  flex-direction: column;
  /* The height is the row's, not a number here: the card fills the row it is in
     and the row is only ever as tall as the tallest card, so the floor below is
     what the card is at rest. That floor is what keeps a deck that was given no
     height at all, the brand kit's, exactly the size it always was. */
  flex: 1 1 auto;
  gap: 0.34rem;
  justify-content: center;
  min-height: 10rem;
  overflow: hidden;
  padding: 0.55rem 0.4rem;
  position: relative;
  /* Square at rest, and turned about its own bottom edge only when the deck
     opens, so the feet stay in a line and only the heads splay. The angle arrives
     as a custom property from the card's place in the deck, and the open rule is
     the only place it is read: nothing here turns a card, because the deck is a
     stack until a pointer arrives. A card whose index was never resolved falls
     back to upright rather than to no angle at all. */
  transform-origin: 50% 100%;
  transition: border-color $dur $ease, box-shadow $dur-slow $ease, transform $dur-slow $ease;

  /* A swatch too dark to read the dark ink on, which is the majority of what a
     palette holds once its brand colour is in it. */
  &.is-ink-light {
    color: $on-dark-strong;
  }
}

/* The code, centred in the card and hidden until the pointer reaches it. It
   never wraps: a code broken across two lines stops reading as one value, so the
   size is what gives on a narrow card instead. At 0.76rem seven characters
   measure about 51px, inside the 72px a desktop card has after its border and
   padding, so the code still holds the card it grew with.

   It takes the ink the chip resolved rather than one of its own, which is what
   makes the pair readable on every colour a palette can hold. It carries no
   position or z-index any more: both existed only to keep it above the scrim,
   and the scrim is gone. */
.swatch-hex {
  font-family: $font-mono;
  font-size: 0.76rem;
  letter-spacing: 0.01em;
  opacity: 0;
  transition: opacity $dur-quick $ease;
  white-space: nowrap;
}

/* The colour's own name, when the caller has one for it, and the quieter line of
   the pair: the code states the value and the name says what it is called. It is
   optional because the brand kit lists codes only: that sheet is a handover
   document for printers and developers, and a name there would be a second word
   for a value the code already states.

   It sits inside the card, under the code, because a deck has no room for a
   caption beneath it. It is revealed with the code on hover rather than held
   outside, so the card stays one object at rest.

   It used to be the softer of the two, at 72% of the ink, and that is the one
   thing it had to give up for the scrim: taking a translucent ink off a swatch
   instead of a scrim is how the pair stops being readable on a mid-tone. So the
   two now share one ink and the step down is carried by type instead, the code in
   the mono face and the name in the sans at a weight above it. */
.swatch-name {
  font-size: 0.74rem;
  font-weight: 600;
  line-height: 1.2;
  opacity: 0;
  overflow-wrap: anywhere;
  text-align: center;
  transition: opacity $dur-quick $ease;
}
</style>

<script>
/* How the deck opens. The arc is the whole fan's spread, end to end, and the
   step is the largest gap allowed between two neighbours; the step that is
   actually used is whichever of the two runs out first.

   Keeping the spread rather than the step as the fixed number is what makes a
   six-colour project behave: the step gives way to the arc, so a longer deck
   fans to the same width as a short one instead of splaying twice as far, and no
   two cards are ever left sharing an angle by a cap. A degree is small and the
   cards are tall and narrow, so it is the heads that travel and the arc only has
   to be wide enough to read as a hand rather than as a stack.

   These two are the only dial on how wide the deck fans, and they answer to
   different things. The step sets how sharply the deck opens, which is what the
   eye reads as the lean, so it is the one to move for a subtler or a bolder fan;
   the arc only ever pulls the step *down*, to stop a six-colour palette splaying
   half again as wide as a four-colour one, so it is a ceiling rather than a
   target and lowering it below the step does nothing on a short deck.

   Both are at zero, which is the deck with no lean at all: the arc is at its
   floor, so the step is zero, every card reads its own angle as zero, and the
   opening is the slide alone. A wing still clears the picker the same distance
   it always did, so the deck still opens as one gesture and the cards still
   separate; they simply stay upright while they do it. It is the honest way to
   try it, because the fan was never anything but these two numbers: putting the
   arc back to 6 and the step to 1.5 restores the lean with no other change, and
   nothing between here and the stylesheet has an opinion about the angle. */
const FAN_ARC = 0;
const FAN_STEP_MAX = 0;

/* Relative luminance and contrast, from WCAG.

   The ink a card is read in has to be chosen from the swatch, and it is not a
   matter of taste: white and near-black each hold up on one end of a palette and
   fail on the other, so a card has to take whichever of the two actually reads.
   Contrast is a ratio between two luminances, so this is the least a colour has
   to be measured for.

   A channel is linearised before it is weighted, because the eye does not read
   raw 0-255 values as proportional: the 0.03928 step is where the sRGB curve
   stops being a straight line, and without it the greens in a palette read far
   brighter than they look. The three weights are the eye's own, green carrying
   most of what we call brightness and blue the least.

   The comparison is made against the two inks' own luminances rather than against
   white and black. That distinction is not academic. The dark ink is #071c38, not
   black, and treating it as black moves the point where the two inks are equally
   readable far enough that six of the fifty-five swatches on the site would be
   handed the wrong one, every one of them the measurably worse of the two. */
function linear(value) {
  const channel = value / 255;

  return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
}

/* A colour's three channels, from either form a stylesheet hands one back in: the
   hexadecimal the tokens are written in, or the `rgb()` a computed custom
   property can come back as. */
function channels(colour) {
  const text = (colour || '').trim();
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(text);

  if (hex) {
    const digits = hex[1];
    const full = digits.length === 3 ? digits.split('').map((digit) => digit + digit).join('') : digits;

    return [0, 2, 4].map((at) => parseInt(full.slice(at, at + 2), 16));
  }

  const rgb = /^rgba?\(([^)]+)\)$/i.exec(text);

  return rgb ? rgb[1].split(/[\s,/]+/).filter(Boolean).slice(0, 3).map(Number) : null;
}

/* Null rather than a number when the colour cannot be read, so an unresolved ink
   is a case the caller answers for instead of quietly counting as black. */
function luminance(colour) {
  const parts = channels(colour);

  if (!parts || parts.some((part) => Number.isNaN(part))) {
    return null;
  }

  return 0.2126 * linear(parts[0]) + 0.7152 * linear(parts[1]) + 0.0722 * linear(parts[2]);
}

/* The WCAG contrast ratio: the lighter luminance over the darker. */
function contrast(a, b) {
  const first = luminance(a);
  const second = luminance(b);

  if (first === null || second === null) {
    return 0;
  }

  return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05);
}

/* Where the two inks are equally readable, as white against black. It is only the
   starting point, for the moment before the stylesheet's own pair has been read,
   and it is what the cards would be judged on if the component were ever rendered
   without its stylesheet at all. */
const INK_CROSSOVER = 0.1791;

export default {
  props: {
    /* [{ hex, name? }]. The hex is what is drawn; `name` is the colour's own
       name and arrives already translated, because only the page knows which
       words its language uses for the palette. */
    swatches: {
      type: Array,
      required: true
    }
  },

  data() {
    /* The card the pointer is on, or null when the deck is shut. It is the only
       state the component holds, and it exists because the fan has to be stepped
       from the picker: which card is the picker is not something a stylesheet can
       know, and a card's angle is meaningless without it.

       The two inks start empty and are filled once the deck is on the page. They
       are read out of the stylesheet rather than restated as hexes here, so the
       tokens stay the only place the colours are declared. */
    return {
      hovered: null,
      inks: { dark: '', light: '' }
    };
  },

  mounted() {
    /* Both inks are already on the deck as custom properties, so the decision
       below can be made against the pair the cards will actually be drawn in. A
       deck is only ever painted once it is in the document, and the detail is
       invisible until the pointer arrives, so there is nothing on screen for this
       to hold up. */
    const styles = getComputedStyle(this.$el);

    this.inks = {
      dark: styles.getPropertyValue('--swatch-ink-dark').trim(),
      light: styles.getPropertyValue('--swatch-ink-light').trim()
    };
  },

  computed: {
    /* The gap between two neighbours when the deck is open. It is derived from
       the palette's length rather than from the pointer, so the deck's shape is
       fixed for a given number of colours and does not breathe as the pointer
       crosses it. */
    step() {
      const gaps = this.swatches.length - 1;

      if (gaps < 1) {
        return 0;
      }

      return Math.min(FAN_STEP_MAX, FAN_ARC / gaps);
    }
  },

  methods: {
    /* The angle a card takes when the deck opens, handed to the stylesheet as the
       custom property it turns the card by. Every card is measured from the
       picker rather than from the middle of the deck, so the picker is the one
       card at zero and the lean grows by an even step either side of it. This is
       the only number on the component that depends on where a card sits, which
       is the whole reason the index is carried. */
    angle(index) {
      if (this.hovered === null) {
        return '0deg';
      }

      return ((index - this.hovered) * this.step).toFixed(2) + 'deg';
    },

    /* Which way a card clears the picker. The stylesheet owns how far, because
       the distance has to answer to the column, so all this gives it is a sign:
       the cards before the picker go one way and the cards after it go the other,
       and the picker itself gets neither. */
    direction(index) {
      if (this.hovered === null || index === this.hovered) {
        return 0;
      }

      return index < this.hovered ? -1 : 1;
    },

    /* Whether this swatch has to be read in the light ink, which is the one
       colour decision the script makes.

       Whichever ink holds the higher contrast against the swatch is the one that
       reads, so the card takes that one and no threshold is involved. The
       comparison is against the two inks the cards are drawn in, which is why they
       are read from the stylesheet rather than assumed: the dark ink is a navy,
       not black, and assuming black is what would hand six swatches on the site
       the worse of the two.

       Until the inks are readable, and if the tokens ever stop arriving, the
       decision falls to the standard white-and-black test. A colour that cannot be
       parsed has no luminance either way, so it falls to the dark ink: an
       unreadable code is worse than a poorly chosen one, and a wrong ink stays
       visible enough to be diagnosed. */
    lightInk(hex) {
      const { dark, light } = this.inks;

      if (dark && light) {
        return contrast(hex, light) > contrast(hex, dark);
      }

      const level = luminance(hex);

      return level !== null && level < INK_CROSSOVER;
    }
  }
};
</script>
